import { Honeybadger } from "@honeybadger-io/react"

import { shouldFilterKnownBrowserNoise } from "./honeybadger.browser.filters"
import {
  groupableUrl,
  sharedNoticeModifiers,
  sharedShouldFilterNotice,
} from "./honeybadger.common"

// handled in _app.tsx
// Honeybadger.configure({
//   apiKey: env("NEXT_PUBLIC_HONEYBADGER_API_KEY"),
//   environment: env("NEXT_PUBLIC_HONEYBADGER_ENV")
//     ? env("NEXT_PUBLIC_HONEYBADGER_ENV")
//     : `local-${process.env.USER}`,
//   revision: env("NEXT_PUBLIC_RELEASE_VERSION"),
//   enableUncaught: true,
//   enableUnhandledRejection: false, // handled in _app.tsx
//   debug: false,
//   // reportData: true, // Uncomment if you want to hit HB in development
// })

Honeybadger.beforeNotify((notice) => {
  if (notice == null) {
    return
  }

  // region Filters

  if (shouldFilterKnownBrowserNoise(notice)) {
    return false
  }

  // I originally kept these, but i think they are not worth the noise.
  if (notice.context?.supported_browser === false) {
    return false
  }

  /*
   * this is a weird one, but these errors come from a .net library that embeds
   * chromium, it looks like for embedded browsers in .net apps or crawlers doing
   * this.
   *
   * It has a very specific error message format, you can see details below.
   *
   * ex: Object Not Found Matching Id:8, MethodName:update, ParamCount:4
   *
   * https://github.com/cefsharp/CefSharp/issues/4209
   */
  if (notice.message && /Object Not Found Matching Id/.test(notice.message)) {
    return false
  }

  /*
   * another weird one:
   *
   * Cannot redefine property: googletag
   *
   * https://stackoverflow.com/questions/78103254/cannot-redefine-property-googletag
   *
   */
  if (
    notice.message &&
    /Cannot redefine property: googletag/.test(notice.message)
  ) {
    return false
  }

  /*
   * This seems to be caused by extensions that modify the DOM in ways
   * that confuse react. WebKit reports the same class of failure with
   * "The object can not be found here."
   */
  if (
    notice.message &&
    (/Failed to execute '(insertBefore|removeChild)' on 'Node'/.test(
      notice.message
    ) ||
      /The object can not be found here\./.test(notice.message))
  ) {
    return false
  }

  // so far haven't found any of these actionable, they could be extension or old browser related
  if (
    notice.name === "window.onerror" &&
    /ResizeObserver/.test(notice.message)
  ) {
    return false
  }

  if (/Network Error/i.test(notice.message)) {
    return false
  }

  // some internal google maps errors, not actionable for us
  if (/EpochMismatch/i.test(notice.message) && /webgl/i.test(notice.stack)) {
    return false
  }
  if (
    /Hung Labeler|LabelerCrash/i.test(notice.message) &&
    /webgl/i.test(notice.stack)
  ) {
    return false
  }
  if (
    /getParameter/i.test(notice.message) &&
    /maps\.googleapis\.com/i.test(notice.stack)
  ) {
    return false
  }
  if (
    /Could not load "(controls|geocoder|geometry|map|marker|onion|poly|stats|util|webgl)"/i.test(
      notice.message
    )
  ) {
    return false
  }

  // browser extension or API issue with postMessage - not actionable
  if (
    /InvalidAccessError/i.test(notice.name) &&
    /postMessage/i.test(notice.stack)
  ) {
    return false
  }

  // createPortal failures from broken/incompatible environments (old browsers, content blockers, stale bundles)
  if (/createPortal.*is not a function/i.test(notice.message)) {
    return false
  }

  // weglot translation service errors - not actionable
  if (
    /TypeError/i.test(notice.name) &&
    /Failed to fetch/i.test(notice.message) &&
    /weglot/i.test(notice.stack)
  ) {
    return false
  }

  // temprorary mute, there are a ton of these due to the new year
  if (
    [notice.name, notice.message].some((str) => /Minified React/i.test(str))
  ) {
    return false
  }

  if (sharedShouldFilterNotice(notice)) {
    return false
  }

  // endregion Filters

  // region Modifiers

  const minifiedReactErrorCapture = /(Minified React error #\d+)/i.exec(
    notice.message
  )?.[1]
  if (minifiedReactErrorCapture) {
    notice.context ??= {}
    notice.context._originalName = notice.name

    notice.name = minifiedReactErrorCapture

    if (notice.url) {
      notice.name += `: ${groupableUrl(notice.url)}`
    }
    return
  }

  return sharedNoticeModifiers(notice)

  // end Modifiers
})
